import {
	IVehicleWheelsSettings,
	VehicleTuningCategory,
	VehicleTuningSubcategory,
	VehicleWheelTireProfile,
	VehicleWheelsSetting,
} from '~/shared';
import { TuningGroup } from '../../tuningGroups.model';
import { createSlice } from '../wrappers';

export enum TuningStudioStage {
	MAIN = 'main',
	PAINT = 'paint',
	TINTING = 'tinting',
	TUNING_MENU = 'tuning_menu',
	EXTERIOR_MENU = 'exterior_menu',
	SUBCATEGORY_MENU = 'subcategory_menu',
}

export interface TuningStudioSlice {
	activeStage: TuningStudioStage;
	activeIndex: number;
	wheelSettings: IVehicleWheelsSettings;
	activeTuningCategory: VehicleTuningCategory | null;
	activeTuningSubcategory: VehicleTuningSubcategory | null;
	activeTuningGroup: TuningGroup | null;
	maxIndex: number;
	interfaceHidden: boolean;
	activeVariants: Record<string, number>;
	tireProfile: VehicleWheelTireProfile;
}

const initialState: TuningStudioSlice = {
	activeStage: TuningStudioStage.MAIN,
	activeIndex: 0,
	wheelSettings: {
		[VehicleWheelsSetting.FRONT_CAMBER]: 1,
		[VehicleWheelsSetting.REAR_CAMBER]: 1,
		[VehicleWheelsSetting.FRONT_OFFSET]: 1,
		[VehicleWheelsSetting.REAR_OFFSET]: 1,
		[VehicleWheelsSetting.RADIUS]: 1,
		[VehicleWheelsSetting.WIDTH]: 1,
	},
	activeTuningCategory: null,
	activeTuningSubcategory: null,
	activeTuningGroup: null,
	maxIndex: 0,
	interfaceHidden: false,
	activeVariants: {},
	tireProfile: VehicleWheelTireProfile.MEDIUM,
};

const tuningStudioSlice = createSlice({
	name: 'tuningStudio',
	initialState,
	reducers: {
		setActiveStage: (state, action) => {
			state.activeStage = action.payload;
		},
		setActiveIndex: (state, action) => {
			state.activeIndex = action.payload;
		},
		setActiveTuningCategory: (state, action) => {
			state.activeTuningCategory = action.payload;
		},
		setActiveTuningSubcategory: (state, action) => {
			state.activeTuningSubcategory = action.payload;
		},
		setActiveTuningGroup: (state, action) => {
			state.activeTuningGroup = action.payload;
		},
		setWheelSettings: (state, action) => {
			state.wheelSettings = action.payload;
		},
		setActiveIndexPrev: (state) => {
			state.activeIndex =
				state.activeIndex > 0 ? state.activeIndex - 1 : state.activeIndex;
		},
		setActiveIndexNext: (state) => {
			state.activeIndex =
				state.activeIndex < state.maxIndex
					? state.activeIndex + 1
					: state.activeIndex;
		},
		setMaxIndex: (state, action) => {
			state.maxIndex = action.payload;
			if (state.activeIndex > state.maxIndex) {
				state.activeIndex = Math.max(0, state.maxIndex);
			}
		},
		setInterfaceHidden: (state, action) => {
			state.interfaceHidden = action.payload;
		},
		setActiveVariant: (
			state,
			action: { payload: { itemId: string; index: number } },
		) => {
			const { itemId, index } = action.payload;
			state.activeVariants[itemId] = index;
		},
		resetActiveVariants: (state) => {
			state.activeVariants = {};
		},
		setTireProfile: (state, action) => {
			state.tireProfile = action.payload;
		},
	},
});
export const {
	setActiveStage,
	setActiveIndex,
	setActiveTuningCategory,
	setActiveTuningSubcategory,
	setActiveTuningGroup,
	setWheelSettings,
	setActiveIndexPrev,
	setActiveIndexNext,
	setMaxIndex,
	setInterfaceHidden,
	setActiveVariant,
	resetActiveVariants,
	setTireProfile,
} = tuningStudioSlice.actions;

export default tuningStudioSlice.reducer;
